import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  type ContentReportStatus,
  contentReportsApi,
} from '~/entities/content-report/api/content-reports'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'

export function useContentReports(status: ContentReportStatus) {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_CONTENT_REPORTS, status),
    queryFn: () => contentReportsApi.list(status),
    enabled: !!session && storageManager.isAdmin(),
  })
}

export function useResolveContentReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => contentReportsApi.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_CONTENT_REPORTS) })
      toast.success(m.contentReportResolved())
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[Content Reports] Resolve failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useDeleteContentReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => contentReportsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_CONTENT_REPORTS) })
      toast.success(m.contentReportDeleted())
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[Content Reports] Delete failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}
